/**
 * Chaplain Form Component
 * Handles prison selection, language/quantity selection, and cart integration
 */

class ChaplainForm extends HTMLElement {
  constructor() {
    super();
    
    // Form state
    this.selectedPrison = null;
    this.selectedVariants = new Map(); // variantId -> {language, quantity}
    this.isDownloadSelected = false;
    this.isPrintSelected = false;
    this.selectedDownloadLanguage = null; // 'english' or 'spanish'
    
    // Product data
    this.productId = this.dataset.productId;
    this.variantId = this.dataset.variantId;
    this.product = null;
    this.variants = [];
    
    // Initialize prison data - will be loaded when needed
    this.prisonData = null;
    
    // Elements will be found in connectedCallback after DOM is ready
    this.elementsInitialized = false;
  }
  
  connectedCallback() {
    // Wait for Liquid content to be rendered
    setTimeout(() => {
      this.initializeElements();
    }, 50);
    
    // Retry with longer delays in case of slow rendering
    setTimeout(() => {
      if (!this.elementsInitialized) {
        this.initializeElements();
      }
    }, 200);
    
    setTimeout(() => {
      if (!this.elementsInitialized) {
        this.initializeElements();
      }
    }, 500);
  }
  
  initializeElements() {
    // Find form elements
    this.form = this.querySelector('form');
    this.prisonSearchInput = this.querySelector('input[name="prison_search"]');
    this.prisonSelect = this.querySelector('input[name="prison_name"]'); // Hidden input
    this.prisonDropdown = this.querySelector('.prison-dropdown');
    this.prisonDropdownList = this.querySelector('.prison-dropdown-list');
    this.prisonNumberInput = this.querySelector('input[name="prison_number"]');
    this.languageSelects = this.querySelectorAll('.language-select');
    this.quantityInputs = this.querySelectorAll('.quantity-input');
    this.downloadRadio = this.querySelector('input[value="download"]');
    this.printRadio = this.querySelector('input[value="print"]');
    this.downloadLabel = this.querySelector('label[for*="download-option"]');
    this.printLabel = this.querySelector('label[for*="print-option"]');
    
    // Language selection elements
    this.downloadLanguageSection = this.querySelector('.download-language-options');
    this.englishLanguageRadio = this.querySelector('input[value="english"]');
    this.spanishLanguageRadio = this.querySelector('input[value="spanish"]');
    this.englishLanguageLabel = this.querySelector('label[for*="language-english"]');
    this.spanishLanguageLabel = this.querySelector('label[for*="language-spanish"]');
    
    this.addToCartBtn = this.querySelector('[id*="add-to-cart-btn"]');
    this.formContainer = this.querySelector('.chaplain-form-container');
    this.printOptionsContainer = this.querySelector('.print-options-container');
    
    // Search state
    this.filteredPrisons = [];
    this.selectedPrisonIndex = -1;
    this.isDropdownOpen = false;
    
    // Check if we found the essential elements
    if (this.prisonSearchInput) {
      this.elementsInitialized = true;
      
      // Initialize the form
      this.init();
      this.setupEventListeners();
      this.populatePrisonData();
    }
  }
  
  init() {
    this.syncDeliveryOptionsWhenSoldOut();
    this.updateFormVisibility();
    this.validateForm();
  }

  isProductAvailable() {
    if (this.dataset.productAvailable !== undefined) {
      return this.dataset.productAvailable !== 'false';
    }
    return this.dataset.printAvailable !== 'false';
  }

  /**
   * Physical print option (follows variant availability from Liquid).
   */
  isPrintOptionAvailable() {
    return this.dataset.printAvailable !== 'false';
  }

  /**
   * Digital PDF language — not tied to inventory; mirrors variant presence in Liquid.
   * @param {'english'|'spanish'|string|null} lang
   */
  isDownloadLanguageAvailable(lang) {
    if (!lang) return false;
    if (lang === 'english') return this.dataset.downloadEnglishAvailable !== 'false';
    if (lang === 'spanish') return this.dataset.downloadSpanishAvailable !== 'false';
    return false;
  }

  soldOutLabel() {
    return this.dataset.soldOutLabel || 'Sold Out';
  }

  syncDeliveryOptionsWhenSoldOut() {
    if (this.isProductAvailable()) return;
    this.isPrintSelected = false;
    this.isDownloadSelected = false;
    this.selectedDownloadLanguage = null;
    if (this.printRadio) {
      this.printRadio.checked = false;
    }
    if (this.downloadRadio) {
      this.downloadRadio.checked = false;
    }
    if (this.printLabel) {
      this.updateSwatchState(this.printLabel, false);
    }
    if (this.downloadLabel) {
      this.updateSwatchState(this.downloadLabel, false);
    }
    if (this.englishLanguageRadio) {
      this.englishLanguageRadio.checked = false;
    }
    if (this.spanishLanguageRadio) {
      this.spanishLanguageRadio.checked = false;
    }
  }
  
  setupEventListeners() {
    // Prison search input
    if (this.prisonSearchInput) {
      this.prisonSearchInput.addEventListener('input', this.handlePrisonSearch.bind(this));
      this.prisonSearchInput.addEventListener('focus', this.handlePrisonSearchFocus.bind(this));
      this.prisonSearchInput.addEventListener('click', this.handlePrisonSearchClick.bind(this));
      this.prisonSearchInput.addEventListener('keydown', this.handlePrisonSearchKeydown.bind(this));
      this.prisonSearchInput.addEventListener('blur', this.handlePrisonSearchBlur.bind(this));
    }
    
    // Click outside to close dropdown
    document.addEventListener('click', this.handleClickOutside.bind(this));
    
    // Download/Print option radio buttons
    if (this.downloadRadio) {
      this.downloadRadio.addEventListener('change', this.handleDownloadChange.bind(this));
    }
    
    if (this.printRadio) {
      this.printRadio.addEventListener('change', this.handlePrintChange.bind(this));
    }
    
    // Language selection for download
    if (this.englishLanguageRadio) {
      this.englishLanguageRadio.addEventListener('change', this.handleLanguageChange.bind(this));
    }
    
    if (this.spanishLanguageRadio) {
      this.spanishLanguageRadio.addEventListener('change', this.handleLanguageChange.bind(this));
    }
    
    
    // Language and quantity changes - updated for new structure
    this.quantityInputs.forEach(input => {
      input.addEventListener('input', this.handleQuantityChange.bind(this));
      input.addEventListener('change', this.handleQuantityChange.bind(this));
    });
    
    // Quantity buttons are handled by the QuantityInput custom element
    // No need to add manual event listeners here
    
    // Add to cart
    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', this.handleAddToCart.bind(this));
    }
    
    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', this.handleFormSubmit.bind(this));
    }
  }
  
  populatePrisonData() {
    // Load prison data from window object
    this.prisonData = window.PRISON_DATA || {};
    
    if (!this.prisonData || Object.keys(this.prisonData).length === 0) {
      // Fallback prison data for testing
      this.prisonData = {
        "Alabama Department of Corrections": "AL001",
        "Alaska Department of Corrections": "AK001",
        "Arizona Department of Corrections": "AZ001",
        "Arkansas Department of Correction": "AR001",
        "California Department of Corrections and Rehabilitation": "CA001",
        "Colorado Department of Corrections": "CO001",
        "Connecticut Department of Correction": "CT001",
        "Delaware Department of Correction": "DE001",
        "Florida Department of Corrections": "FL001",
        "Georgia Department of Corrections": "GA001",
        "Hawaii Department of Public Safety": "HI001",
        "Idaho Department of Correction": "ID001",
        "Illinois Department of Corrections": "IL001",
        "Indiana Department of Correction": "IN001",
        "Iowa Department of Corrections": "IA001",
        "Kansas Department of Corrections": "KS001",
        "Kentucky Department of Corrections": "KY001",
        "Louisiana Department of Public Safety & Corrections": "LA001",
        "Maine Department of Corrections": "ME001",
        "Maryland Department of Public Safety and Correctional Services": "MD001",
        "Massachusetts Department of Correction": "MA001",
        "Michigan Department of Corrections": "MI001",
        "Minnesota Department of Corrections": "MN001",
        "Mississippi Department of Corrections": "MS001",
        "Missouri Department of Corrections": "MO001",
        "Montana Department of Corrections": "MT001",
        "Nebraska Department of Correctional Services": "NE001",
        "Nevada Department of Corrections": "NV001",
        "New Hampshire Department of Corrections": "NH001",
        "New Jersey Department of Corrections": "NJ001",
        "New Mexico Corrections Department": "NM001",
        "New York State Department of Corrections and Community Supervision": "NY001",
        "North Carolina Department of Public Safety": "NC001",
        "North Dakota Department of Corrections and Rehabilitation": "ND001",
        "Ohio Department of Rehabilitation and Correction": "OH001",
        "Oklahoma Department of Corrections": "OK001",
        "Oregon Department of Corrections": "OR001",
        "Pennsylvania Department of Corrections": "PA001",
        "Rhode Island Department of Corrections": "RI001",
        "South Carolina Department of Corrections": "SC001",
        "South Dakota Department of Corrections": "SD001",
        "Tennessee Department of Correction": "TN001",
        "Texas Department of Criminal Justice": "TX001",
        "Utah Department of Corrections": "UT001",
        "Vermont Department of Corrections": "VT001",
        "Virginia Department of Corrections": "VA001",
        "Washington State Department of Corrections": "WA001",
        "West Virginia Division of Corrections": "WV001",
        "Wisconsin Department of Corrections": "WI001",
        "Wyoming Department of Corrections": "WY001"
      };
    }
    
    // Initialize with all prisons
    this.filteredPrisons = Object.keys(this.prisonData).sort();
    
    // Pre-render the dropdown so it's ready for first focus
    this.renderDropdown();
  }

  handlePrisonSearch(event) {
    const searchTerm = event.target.value.toLowerCase().trim();
    
    if (searchTerm.length === 0) {
      this.filteredPrisons = Object.keys(this.prisonData).sort();
    } else {
      this.filteredPrisons = Object.keys(this.prisonData)
        .filter(prisonName => 
          prisonName.toLowerCase().includes(searchTerm) ||
          this.prisonData[prisonName].toLowerCase().includes(searchTerm)
        )
        .sort();
    }
    
    this.selectedPrisonIndex = -1;
    this.renderDropdown();
    this.showDropdown();
  }
  
  handlePrisonSearchFocus(event) {
    // Ensure we have all prisons loaded when focusing for the first time
    if (this.filteredPrisons.length === 0) {
      this.filteredPrisons = Object.keys(this.prisonData).sort();
    }
    this.renderDropdown();
    this.showDropdown();
  }
  
  handlePrisonSearchClick(event) {
    // Ensure we have all prisons loaded when clicking for the first time
    if (this.filteredPrisons.length === 0) {
      this.filteredPrisons = Object.keys(this.prisonData).sort();
    }
    this.renderDropdown();
    this.showDropdown();
  }
  
  handlePrisonSearchKeydown(event) {
    if (!this.isDropdownOpen) return;
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedPrisonIndex = Math.min(this.selectedPrisonIndex + 1, this.filteredPrisons.length - 1);
        this.updateDropdownSelection();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedPrisonIndex = Math.max(this.selectedPrisonIndex - 1, -1);
        this.updateDropdownSelection();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectedPrisonIndex >= 0 && this.selectedPrisonIndex < this.filteredPrisons.length) {
          this.selectPrison(this.filteredPrisons[this.selectedPrisonIndex]);
        }
        break;
      case 'Escape':
        this.hideDropdown();
        break;
    }
  }
  
  handlePrisonSearchBlur(event) {
    // Delay hiding to allow for click events on dropdown items
    setTimeout(() => {
      this.hideDropdown();
    }, 150);
  }
  
  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      this.hideDropdown();
    }
  }
  
  showDropdown() {
    if (this.prisonDropdown) {
      this.prisonDropdown.style.display = 'block';
      this.isDropdownOpen = true;
    }
  }
  
  hideDropdown() {
    if (this.prisonDropdown) {
      this.prisonDropdown.style.display = 'none';
      this.isDropdownOpen = false;
      this.selectedPrisonIndex = -1;
    }
  }
  
  renderDropdown() {
    if (!this.prisonDropdownList) return;
    
    this.prisonDropdownList.innerHTML = '';
    
    if (this.filteredPrisons.length === 0) {
      const noResults = document.createElement('div');
      noResults.className = 'no-results';
      noResults.textContent = 'No prisons found';
      this.prisonDropdownList.appendChild(noResults);
      return;
    }
    
    this.filteredPrisons.forEach((prisonName, index) => {
      const option = document.createElement('div');
      option.className = 'prison-option';
      option.dataset.index = index;
      option.innerHTML = `
        <div class="prison-name">${prisonName}</div>
        <div class="prison-number">${this.prisonData[prisonName]}</div>
      `;
      
      option.addEventListener('click', () => {
        this.selectPrison(prisonName);
      });
      
      this.prisonDropdownList.appendChild(option);
    });
  }
  
  updateDropdownSelection() {
    const options = this.prisonDropdownList.querySelectorAll('.prison-option');
    options.forEach((option, index) => {
      option.classList.toggle('selected', index === this.selectedPrisonIndex);
    });
  }
  
  selectPrison(prisonName) {
    this.selectedPrison = {
      name: prisonName,
      number: this.prisonData[prisonName]
    };
    
    // Update search input
    if (this.prisonSearchInput) {
      this.prisonSearchInput.value = prisonName;
    }
    
    // Update hidden input
    if (this.prisonSelect) {
      this.prisonSelect.value = prisonName;
    }
    
    // Auto-populate prison number
    if (this.prisonNumberInput) {
      this.prisonNumberInput.value = this.selectedPrison.number;
    }
    
    this.hideDropdown();
    this.updateFormVisibility();
    this.validateForm();
  }
  
  updatePdfContent(prisonName, prisonNumber, language, element) {
    // Update prison name and number in PDF content
    const pdfPrisonName = element.querySelector('#pdfPrisonName, #pdfPrisonNameSpanish');
    const pdfPrisonNumber = element.querySelector('#pdfPrisonNumber, #pdfPrisonNumberSpanish');
    const pdfPrisonNameField = element.querySelector('#pdfPrisonNameField, #pdfPrisonNameFieldSpanish');
    const pdfFormPrisonName = element.querySelector('#pdfFormPrisonName, #pdfFormPrisonNameSpanish');
    const pdfFormPrisonNumber = element.querySelector('#pdfFormPrisonNumber, #pdfFormPrisonNumberSpanish');
    const pdfFormPrisonerId = element.querySelector('#pdfFormPrisonerId, #pdfFormPrisonerIdSpanish');
    const pdfPrisonNumberFooter = element.querySelector('#pdfPrisonNumberFooter, #pdfPrisonNumberFooterSpanish');
    const pdfPrisonNumberPage2 = element.querySelector('#pdfPrisonNumberPage2, #pdfPrisonNumberPage2Spanish');
    
    // Update all prison-related elements
    if (pdfPrisonName) pdfPrisonName.textContent = prisonName || 'MA-Boston Pre Release Center';
    if (pdfPrisonNumber) pdfPrisonNumber.textContent = prisonNumber || '1212';
    if (pdfPrisonNameField) pdfPrisonNameField.textContent = prisonName || 'MA-Boston Pre Release Center';
    if (pdfFormPrisonName) pdfFormPrisonName.textContent = prisonName || 'MA-Boston Pre Release Center';
    if (pdfFormPrisonNumber) pdfFormPrisonNumber.textContent = prisonNumber || '1212';
    if (pdfFormPrisonerId) pdfFormPrisonerId.textContent = prisonNumber || '1212';
    if (pdfPrisonNumberFooter) pdfPrisonNumberFooter.textContent = prisonNumber || '1212';
    if (pdfPrisonNumberPage2) pdfPrisonNumberPage2.textContent = prisonNumber || '1212';
  }
  
  handleDownloadChange(event) {
    this.isDownloadSelected = event.target.checked;
    
    // If download is selected, deselect print option
    if (this.isDownloadSelected) {
      this.isPrintSelected = false;
      if (this.printRadio) {
        this.printRadio.checked = false;
      }
      this.updateSwatchState(this.printLabel, false);
    }
    
    // Update swatch state
    this.updateSwatchState(this.downloadLabel, this.isDownloadSelected);
    
    // Update form visibility
    this.updateFormVisibility();
    this.validateForm();
  }
  
  handleLanguageChange(event) {
    if (event.target.disabled) {
      return;
    }
    const value = event.target.value;
    this.selectedDownloadLanguage = this.isDownloadLanguageAvailable(value) ? value : null;
    if (!this.isDownloadLanguageAvailable(value)) {
      event.target.checked = false;
    }

    // Update swatch states
    this.updateSwatchState(this.englishLanguageLabel, this.englishLanguageRadio && this.englishLanguageRadio.checked);
    this.updateSwatchState(this.spanishLanguageLabel, this.spanishLanguageRadio && this.spanishLanguageRadio.checked);

    // Validate form
    this.validateForm();
  }
  
  handlePrintChange(event) {
    if (!this.isPrintOptionAvailable()) {
      return;
    }
    this.isPrintSelected = event.target.checked;
    
    // If print is selected, deselect download option
    if (this.isPrintSelected && this.isDownloadSelected) {
      this.isDownloadSelected = false;
      this.selectedDownloadLanguage = null;
      if (this.downloadRadio) {
        this.downloadRadio.checked = false;
      }
      this.updateSwatchState(this.downloadLabel, false);
    }
    
    // Update swatch state
    this.updateSwatchState(this.printLabel, this.isPrintSelected);
    
    // Update form visibility
    this.updateFormVisibility();
    this.validateForm();
  }
  
  updateSwatchState(label, isSelected) {
    if (!label) return;
    
    if (isSelected) {
      label.classList.add('selected');
      label.classList.add('active');
    } else {
      label.classList.remove('selected');
      label.classList.remove('active');
    }
  }
  
  
  
  handleQuantityChange(event) {
    const variantRow = event.target.closest('[data-variant-id]');
    if (!variantRow) return;
    if (event.target.disabled) {
      return;
    }
    const variantId = variantRow.dataset.variantId;
    const language = variantRow.dataset.language;
    let quantity = parseInt(event.target.value, 10) || 0;
    const maxRaw = variantRow.dataset.qtyMax;
    const max = maxRaw !== undefined && maxRaw !== '' ? parseInt(maxRaw, 10) : NaN;
    if (!Number.isNaN(max) && quantity > max) {
      quantity = max;
      event.target.value = String(max);
    }

    if (variantId && quantity > 0) {
      this.selectedVariants.set(variantId, { language, quantity });
    } else if (variantId) {
      this.selectedVariants.delete(variantId);
    }

    this.updateVariantSummary();
    this.validateForm();
  }
  
  
  updateVariantSummary() {
    const summaryElement = this.querySelector('#variant-summary-content');
    if (!summaryElement) return;
    
    if (this.selectedVariants.size === 0) {
      summaryElement.innerHTML = '<p class="text-muted mb-0">No forms selected</p>';
      return;
    }
    
    let summaryHTML = '<div class="row">';
    this.selectedVariants.forEach((variantData, variantId) => {
      const { language, quantity } = variantData;
      const badgeClass = language === 'English' ? 'bg-primary' : 'bg-success';
      summaryHTML += `
        <div class="col-md-6 mb-2">
          <span class="badge ${badgeClass} me-2">${language}</span>
          <span class="fw-semibold">${quantity} form${quantity > 1 ? 's' : ''}</span>
        </div>
      `;
    });
    summaryHTML += '</div>';
    
    summaryElement.innerHTML = summaryHTML;
  }

  updateFormVisibility() {
    // Show form sections based on selections
    if (this.formContainer) {
      // Always show the form container, but control individual sections within it
      this.formContainer.style.display = 'block';
      
      // Control visibility of sections within the form container
      const optionsSection = this.formContainer.querySelector('.options-section');
      const cartSection = this.formContainer.querySelector('.cart-section');
      
      if (optionsSection) {
        const showOptions = this.selectedPrison !== null;
        optionsSection.style.display = showOptions ? 'block' : 'none';
      }
      
      if (cartSection) {
        const showCart = this.selectedPrison !== null && (this.isDownloadSelected || this.isPrintSelected);
        cartSection.style.display = showCart ? 'block' : 'none';
      }
    }
    
    // Show language selection only if download is selected
    if (this.downloadLanguageSection) {
      const showLanguageOptions = this.isDownloadSelected;
      this.downloadLanguageSection.style.display = showLanguageOptions ? 'block' : 'none';
    }
    
    // Show print options only if print is selected AND download is NOT selected
    if (this.printOptionsContainer) {
      const showPrintOptions = this.isPrintSelected && !this.isDownloadSelected;
      this.printOptionsContainer.style.display = showPrintOptions ? 'block' : 'none';
      
      // If hiding print options, clear any selected languages/quantities
      if (!showPrintOptions) {
        this.clearPrintSelections();
      }
    }
    
  }

  clearPrintSelections() {
    // Clear selected variants map (only print-related selections)
    this.selectedVariants.clear();
    
    // Reset quantity inputs
    this.quantityInputs.forEach(input => {
      input.value = '0';
    });
    
    // Update summary
    this.updateVariantSummary();
  }

  validateForm() {
    let isValid = false;

    if (!this.isProductAvailable()) {
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.classList.remove('btn-primary');
        this.addToCartBtn.classList.add('btn-secondary');
        const btnText = this.addToCartBtn.querySelector('.btn-text');
        if (btnText) {
          btnText.textContent = this.soldOutLabel();
        }
      }
      return false;
    }
    
    // Check if prison is selected
    if (!this.selectedPrison) {
      isValid = false;
    }
    // Check if at least one option (download or print) is selected
    else if (!this.isDownloadSelected && !this.isPrintSelected) {
      isValid = false;
    }
    // If download is selected (print should be automatically deselected)
    else if (this.isDownloadSelected) {
      isValid =
        this.selectedDownloadLanguage !== null &&
        this.isDownloadLanguageAvailable(this.selectedDownloadLanguage);
    }
    // If print is selected (download should be automatically deselected)
    else if (this.isPrintSelected) {
      if (this.selectedVariants.size === 0) {
        isValid = false;
      } else {
        isValid = Array.from(this.selectedVariants.entries()).every(([id, variantData]) => {
          if (variantData.quantity <= 0) return false;
          const row = this.querySelector(`[data-variant-id="${id}"]`);
          if (!row) return false;
          const input = row.querySelector('[data-qty-input]');
          if (input && input.disabled) return false;
          const max = parseInt(row.dataset.qtyMax, 10);
          if (!Number.isNaN(max) && variantData.quantity > max) return false;
          return true;
        });
      }
    }
    
    // Update add to cart button state
    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = !isValid;
      this.addToCartBtn.classList.toggle('btn-primary', isValid);
      this.addToCartBtn.classList.toggle('btn-secondary', !isValid);
      
      // Update button text based on selection
      const btnText = this.addToCartBtn.querySelector('.btn-text');
      if (btnText) {
        if (this.isDownloadSelected && !this.isPrintSelected) {
          btnText.textContent = 'Download Form';
        } else {
          btnText.textContent = 'Add to Cart';
        }
      }
    }
    
    return isValid;
  }
  
  handleAddToCart(event) {
    event.preventDefault();

    if (!this.isProductAvailable()) {
      return;
    }
    
    if (!this.validateForm()) {
      this.showError('Please complete all required fields before adding to cart.');
      return;
    }
    
    // If download is selected, trigger download instead of cart
    if (this.isDownloadSelected && !this.isPrintSelected) {
      this.downloadForm();
      return; // Exit early - no cart addition for downloads
    }
    
    // Only proceed with cart addition for print orders
    if (this.isPrintSelected && !this.isDownloadSelected) {
      // Prepare cart data for print orders
      const cartItems = this.prepareCartData();
      
      // Add items to cart
      this.addItemsToCart(cartItems);
    }
  }
  
  handleFormSubmit(event) {
    event.preventDefault();
    this.handleAddToCart(event);
  }

  downloadForm() {
    if (!this.isProductAvailable()) {
      return;
    }
    if (!this.selectedPrison) {
      this.showError('Please select a prison before downloading the form.');
      return;
    }

    if (!this.selectedDownloadLanguage) {
      this.showError('Please select a language for the download.');
      return;
    }

    if (!this.isDownloadLanguageAvailable(this.selectedDownloadLanguage)) {
      this.showError('The selected language is not available.');
      return;
    }

    // Check if mobile device
    const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // For mobile: directly download PDF without showing modal
      this.downloadPdfDirectly(this.selectedPrison.name, this.selectedPrison.number, this.selectedDownloadLanguage);
    } else {
      // For desktop: show modal with preview
      if (typeof window.openChaplainPdfModal === 'function') {
        window.openChaplainPdfModal(this.selectedPrison.name, this.selectedPrison.number, this.selectedDownloadLanguage);
      } else {
        // Try to wait a bit for the modal to load
        setTimeout(() => {
          if (typeof window.openChaplainPdfModal === 'function') {
            window.openChaplainPdfModal(this.selectedPrison.name, this.selectedPrison.number, this.selectedDownloadLanguage);
          } else {
            this.showError('PDF system not loaded. Please refresh the page and try again.');
          }
        }, 1000);
      }
    }
  }

  async downloadPdfDirectly(prisonName, prisonNumber, language) {
    try {
      // Show loading state
      this.setLoadingState(true);
      
      // Get PDF content element based on language
      const elementId = language === 'spanish' ? 'chaplainPdfContentSpanish' : 'chaplainPdfContent';
      const element = document.getElementById(elementId);
      if (!element) {
        throw new Error(`PDF content not found for language: ${language}`);
      }

      // Update PDF content with prison information
      this.updatePdfContent(prisonName, prisonNumber, language, element);

      // Make element visible for PDF generation
      element.classList.remove('hidden');
      element.style.position = 'absolute';
      element.style.left = '-9999px';
      element.style.top = '-9999px';
      element.style.visibility = 'visible';
      element.style.display = 'block';
      element.style.zIndex = '-1';

      // Wait for html2pdf to load
      if (typeof html2pdf === 'undefined') {
        await new Promise(resolve => {
          const checkHtml2pdf = setInterval(() => {
            if (typeof html2pdf !== 'undefined') {
              clearInterval(checkHtml2pdf);
              resolve();
            }
          }, 100);
        });
      }

      // Generate PDF
      const opt = {
        margin: 0.5,
        filename: `chaplain-form-${language}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2, 
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: element.scrollWidth,
          height: element.scrollHeight,
          scrollX: 0,
          scrollY: 0
        },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      // Generate and download PDF
      await html2pdf().set(opt).from(element).save();
      
      // Hide element again
      element.classList.add('hidden');
      element.style.position = '';
      element.style.left = '';
      element.style.top = '';
      element.style.visibility = '';
      element.style.display = '';
      element.style.zIndex = '';

      this.setLoadingState(false);
      this.showSuccess('PDF downloaded successfully!');

    } catch (error) {
      console.error('Error downloading PDF:', error);
      this.setLoadingState(false);
      this.showError('Failed to download PDF. Please try again.');
    }
  }



  
  prepareCartData() {
    const items = [];

    if (this.isDownloadSelected) {
      let downloadVariantId = this.variantId;
      if (this.selectedDownloadLanguage === 'english' && this.dataset.englishVariantId) {
        downloadVariantId = this.dataset.englishVariantId;
      } else if (this.selectedDownloadLanguage === 'spanish' && this.dataset.spanishVariantId) {
        downloadVariantId = this.dataset.spanishVariantId;
      }

      items.push({
        id: parseInt(downloadVariantId, 10),
        quantity: 1,
        properties: {
          'Prison Name': this.selectedPrison.name,
          'Prison Number': this.selectedPrison.number
        }
      });
    }

    if (this.isPrintSelected && this.selectedVariants.size > 0) {
      this.selectedVariants.forEach((variantData, variantId) => {
        const row = this.querySelector(`[data-variant-id="${variantId}"]`);
        let qty = parseInt(variantData.quantity, 10);
        if (row) {
          const max = parseInt(row.dataset.qtyMax, 10);
          if (!Number.isNaN(max) && qty > max) {
            qty = max;
          }
        }
        if (qty > 0) {
          items.push({
            id: parseInt(variantId, 10),
            quantity: qty,
            properties: {
              'Prison Name': this.selectedPrison.name,
              'Prison Number': this.selectedPrison.number
            }
          });
        }
      });
    }

    return items;
  }
  
  async addItemsToCart(items) {
    if (!items || items.length === 0) {
      this.showError('No items to add to cart.');
      return;
    }
    
    // Show loading state
    this.setLoadingState(true);
    
    try {
      // Add items sequentially to avoid cart conflicts
      for (const item of items) {
        await this.addSingleItemToCart(item);
      }
      
      // Show success message
      this.showSuccess('Items added to cart successfully!');
      
      // Reset form
      this.resetForm();
      
      // Update cart UI
      this.updateCartDisplay();
      
    } catch (error) {
      this.showError('Failed to add items to cart. Please try again.');
    } finally {
      this.setLoadingState(false);
    }
  }
  
  async addSingleItemToCart(item) {
    const formData = new FormData();
    formData.append('id', item.id);
    formData.append('quantity', item.quantity);
    
    // Add properties
    if (item.properties) {
      Object.entries(item.properties).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, value);
      });
    }
    
    // Add cart section for updates
    const cartElement = document.querySelector('cart-form');
    const sectionId = cartElement?.dataset?.sectionId || 'template-cart';
    formData.append('sections', sectionId);
    
    const fetchConfig = {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    };
    
    const response = await fetch('/cart/add.js', fetchConfig);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (result.errors) {
      throw new Error(result.errors);
    }
    
    return result;
  }
  
  updateCartDisplay() {
    // Update cart count in header
    if (typeof updateCartCountInHeader === 'function') {
      updateCartCountInHeader();
    }
    
    // Update cart drawer if present
    const cartElement = document.querySelector('cart-form');
    if (cartElement && typeof cartElement._updateCart === 'function') {
      // Fetch updated cart data
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          window.globalSpace = window.globalSpace || {};
          window.globalSpace.cart = cart;
        })
        .catch(error => {});
    }
  }
  
  resetForm() {
    // Reset form state
    this.selectedPrison = null;
    this.selectedVariants.clear();
    this.isDownloadSelected = false;
    this.isPrintSelected = false;
    this.selectedDownloadLanguage = null;
    
    // Reset search elements
    if (this.prisonSearchInput) {
      this.prisonSearchInput.value = '';
    }
    
    if (this.prisonSelect) {
      this.prisonSelect.value = '';
    }
    
    if (this.prisonNumberInput) {
      this.prisonNumberInput.value = '';
    }
    
    // Hide dropdown
    this.hideDropdown();
    
    // Reset language selects and quantity inputs
    this.languageSelects.forEach(select => {
      select.value = '';
    });
    
    this.quantityInputs.forEach(input => {
      input.value = '';
      input.disabled = true;
      input.required = false;
    });
    
    // Reset radio buttons
    if (this.downloadRadio) {
      this.downloadRadio.checked = false;
    }
    if (this.printRadio) {
      this.printRadio.checked = false;
    }
    if (this.englishLanguageRadio) {
      this.englishLanguageRadio.checked = false;
    }
    if (this.spanishLanguageRadio) {
      this.spanishLanguageRadio.checked = false;
    }
    
    // Reset swatch states
    this.updateSwatchState(this.downloadLabel, false);
    this.updateSwatchState(this.printLabel, false);
    this.updateSwatchState(this.englishLanguageLabel, false);
    this.updateSwatchState(this.spanishLanguageLabel, false);
    
    // Update form visibility and validation
    this.updateFormVisibility();
    this.validateForm();
  }
  
  setLoadingState(loading) {
    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = loading;
      
      const spinner = this.addToCartBtn.querySelector('.spinner-border');
      const text = this.addToCartBtn.querySelector('.btn-text');
      
      if (loading) {
        if (spinner) spinner.style.display = 'inline-block';
        if (text) {
          // Set appropriate loading text based on selection
          if (this.isDownloadSelected && !this.isPrintSelected) {
            text.textContent = 'Generating PDF...';
          } else {
            text.textContent = 'Adding to Cart...';
          }
        }
      } else {
        if (spinner) spinner.style.display = 'none';
        if (text) {
          if (!this.isProductAvailable()) {
            text.textContent = this.soldOutLabel();
          } else if (this.isDownloadSelected && !this.isPrintSelected) {
            text.textContent = 'Download Form';
          } else {
            text.textContent = 'Add to Cart';
          }
        }
      }
    }
  }
  
  showSuccess(message) {
    // Use existing utility function if available
    if (typeof Utility !== 'undefined' && typeof Utility.handleSuccess === 'function') {
      Utility.handleSuccess('Success', message);
    } else {
      // Fallback to simple alert
      alert(message);
    }
  }
  
  showError(message) {
    // Use existing utility function if available
    if (typeof Utility !== 'undefined' && typeof Utility.handleError === 'function') {
      Utility.handleError(message, 'Chaplain Form Error');
    } else {
      // Fallback to simple alert
      alert('Error: ' + message);
    }
  }

}

// Register the custom element
customElements.define('chaplain-form', ChaplainForm);

// Fallback PDF modal function (in case snippets don't load)
if (typeof window.openChaplainPdfModal === 'undefined') {
  window.openChaplainPdfModal = function(prisonName, prisonNumber) {
    alert(`PDF Modal not loaded properly.\n\nPrison: ${prisonName}\nNumber: ${prisonNumber}\n\nPlease refresh the page and try again.`);
  };
}

// Fallback: Traditional DOM ready approach
document.addEventListener('DOMContentLoaded', function() {
  // Wait a bit for Liquid rendering
  setTimeout(() => {
    const prisonSearchInput = document.querySelector('input[name="prison_search"]');
    
    if (prisonSearchInput && !prisonSearchInput.dataset.initialized) {
      // Mark as initialized to prevent duplicate initialization
      prisonSearchInput.dataset.initialized = 'true';
      
      // Get prison data
      const prisonData = window.PRISON_DATA || {
        "Alabama Department of Corrections": "AL001",
        "Alaska Department of Corrections": "AK001",
        "Arizona Department of Corrections": "AZ001",
        "Arkansas Department of Correction": "AR001",
        "California Department of Corrections and Rehabilitation": "CA001",
        "Colorado Department of Corrections": "CO001",
        "Connecticut Department of Correction": "CT001",
        "Delaware Department of Correction": "DE001",
        "Florida Department of Corrections": "FL001",
        "Georgia Department of Corrections": "GA001"
      };
      
      // Initialize search functionality if custom element didn't load
      if (typeof ChaplainForm === 'undefined') {
        console.log('ChaplainForm custom element not found, initializing fallback search');
        // The search functionality will be handled by the custom element
        // This fallback is mainly for ensuring prison data is available
      }
    }
  }, 100);
});
